import React, { JSX } from 'react';
import {
  ImageField,
  Field,
  LinkField,
  Link,
  RichText,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { StyleguideComponentProps } from 'lib/component-props';
import Image from 'components/atoms/Image';
import { ItemValue } from 'components/types/ListItem';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import VideoPlayer from 'components/molecules/VideoPlayer';

type ContentListingProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    BaseImage: ImageField;
    BaseImageRight: {
      value: boolean;
    };
    BaseHeading: Field<string>;
    BaseHeadingH3: {
      value: boolean;
    };
    BaseDescription: Field<string>;
    BaseBackground: {
      value: boolean;
    };
    BaseLink: LinkField;
    BaseLinkType: ItemValue;
    BaseVideoUrl: Field<string>;
    TextAlignment: ItemValue;
    OnCallBottomGradient: {
      value: boolean;
    };
    OnCallBackgroundVertical: ImageField;
    OnCallBackgroundHorizontal: ImageField;
    BasePhoneNumber: Field<string>;
  };
};

const ContentListing = (props: ContentListingProps): JSX.Element => {
  if (
    props.fields == undefined ||
    !props.fields.BaseHeading ||
    !props.fields.BaseDescription
  ) {
    return <div>Data source is not defined</div>;
  }

  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );

  const {
    BaseImage,
    BaseImageRight,
    BaseHeading,
    BaseHeadingH3,
    BaseDescription,
    BaseBackground,
    BaseLink,
    BaseLinkType,
    BaseVideoUrl,
    BasePhoneNumber,
  } = props?.fields;

  const CenteredMobile =
    props?.fields?.TextAlignment?.name?.toLowerCase() === 'centered';

  const isOnCallBottomGradient = props.fields['OnCallBottomGradient']?.value === true;

  const verticalSrc = props.fields?.OnCallBackgroundVertical?.value?.src as string | undefined;
  const horizontalSrc = props.fields?.OnCallBackgroundHorizontal?.value?.src as string | undefined;
  const hasBackgroundImages = verticalSrc || horizontalSrc;
  const phoneNumber = BasePhoneNumber?.value?.trim();
  const linkText = BaseLink?.value?.text?.trim();
  const linkClassName = classNames(
    `btn-link btn-${BaseLinkType?.name?.toLowerCase()}`,
    BaseLinkType?.name?.toLowerCase() !== 'tertiary'
      ? 'btn-mobile-fw'
      : ''
  );

  return (
    <div
      className={classNames('overhauled', marginSize, isOnCallBottomGradient ? 'oncall-gradient' : '', hasBackgroundImages ? 'has-oncall-bg' : '')}
      id={`content-listing-${props.rendering?.uid}`}
      style={{
        ...(verticalSrc && { '--oncall-bg-vertical': `url('${verticalSrc}')` } as React.CSSProperties),
        ...(horizontalSrc && { '--oncall-bg-horizontal': `url('${horizontalSrc}')` } as React.CSSProperties),
      }}
    >
      <div className="content-listing">
        <div
          className={`bg-wrapper ${BaseImageRight?.value == true ? 'img-right' : ''} ${
            BaseBackground?.value == true ? 'bg-secondary' : ''
          } ${
            BaseBackground?.value == true || hasBackgroundImages || isOnCallBottomGradient
              ? 'inner-vertical-spacing'
              : ''
          }`}
        >
          <div className="jss-inner-container">
            <div className="inner-content">
              {BaseImage?.value?.src && !BaseVideoUrl.value && (
                <div className="listing-img">
                  <Image
                    imageUrl={BaseImage?.value?.src as string}
                    alt={
                      (BaseImage?.value?.alt as string) != ''
                        ? (BaseImage?.value?.alt as string)
                        : 'Image'
                    }
                  />
                </div>
              )}
              {BaseImage?.value?.src && BaseVideoUrl?.value && (
                <div className="listing-img">
                  <VideoPlayer
                    VideoUrl={BaseVideoUrl}
                    Thumbnail={BaseImage}
                    allowFullScreen
                  ></VideoPlayer>
                </div>
              )}
              <div
                className={classNames(
                  'listing-content',
                  BaseImage?.value?.src ? 'with-img' : '',
                  CenteredMobile ? 'align-center-mobile' : ''
                )}
              >
                {BaseHeading && BaseHeadingH3?.value == true ? (
                  <h3 className="listing-heading osf-display-h3">
                    {BaseHeading?.value}
                  </h3>
                ) : (
                  <h2 className="listing-heading">{BaseHeading?.value}</h2>
                )}
                <RichText
                  tag=""
                  className="listing-description osf-body-copy"
                  field={BaseDescription}
                />
                {BaseLinkType?.name &&
                (phoneNumber ? (
                  <a
                    href={`tel:${phoneNumber}`}
                    className={linkClassName}
                  >
                    {linkText || phoneNumber}
                  </a>
                ) : (
                  BaseLink?.value?.href && (
                    <Link
                      className={linkClassName}
                      field={BaseLink}
                    />
                  )
                ))}
              </div>
            </div>
          </div>
          {isOnCallBottomGradient && (<div className="overhauled bottom-border gradient-horizontal"></div>)}
        </div>
      </div>
    </div>
  );
};

export default ContentListing;
